import './FamilyLink.scss';

import { type MouseEvent, PureComponent } from 'react';
import { Link } from 'react-router';
import Measure from 'react-measure';
import classNames from 'classnames';
import { Translation } from 'react-i18next';

import ColorBar from 'components/ColorBar';
import linearScale from 'utils/linearScale';

const DEVICE_ORIENTATION_DOMAIN: [number, number] = [-20, 20];

interface Props {
  family: {
    name: string;
    slug: string;
    conceptFont: boolean;
    variableWeightMin: number;
    variableWeightMax: number;
    variableLineHeight: number;
    variableMarginBottom: number;
    homePageTextColor: string | null;
    facesCount: number;
    widthsCount: number;
    variableFontsCount: number;
  };
}

interface State {
  weight: number;
  slant: number;
  dimensions?: {
    width: number;
    height: number;
    top: number;
    left: number;
  };
}

export class FamilyLink extends PureComponent<Props, State> {
  static displayName = 'Families.FamilyLink';

  state = {
    weight: 0,
    slant: 0,
    dimensions: { top: 0, left: 0, width: 0, height: 0 },
  };

  componentDidMount() {
    window.addEventListener(
      'deviceorientation',
      this.handleDeviceOrientation,
      true,
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'deviceorientation',
      this.handleDeviceOrientation,
      true,
    );
  }

  handleMouseMove = (event: MouseEvent) => {
    const { family } = this.props;
    const { width, height, top, left } = this.state.dimensions;

    const scaleWeight = linearScale(
      [0, width],
      [family.variableWeightMin, family.variableWeightMax],
    );

    const scaleSlant = linearScale([0, height], [0, 90]);

    const { pageX, pageY } = event;

    this.setState({
      weight: scaleWeight(pageX - left),
      slant: scaleSlant(pageY - top),
    });
  };

  handleDeviceOrientation = (event: DeviceOrientationEvent) => {
    const { beta, gamma } = event;
    const { family } = this.props;

    if (beta == null || gamma == null) {
      return;
    }

    const scaleWeight = linearScale(DEVICE_ORIENTATION_DOMAIN, [
      family.variableWeightMin,
      family.variableWeightMax,
    ]);

    const scaleSlant = linearScale(DEVICE_ORIENTATION_DOMAIN, [0, 90]);

    this.setState({
      weight: scaleWeight(gamma),
      slant: scaleSlant(beta),
    });
  };

  render() {
    const { family } = this.props;
    const { weight, slant } = this.state;

    return (
      <Measure
        bounds
        onResize={(contentRect) => {
          this.setState({ dimensions: contentRect.bounds });
        }}
      >
        {({ measureRef }) => (
          <ColorBar>
            <div
              ref={measureRef}
              className={classNames(
                'FamiliesFamilyLink',
                'content-wrapper',
                `FamiliesFamilyLink--${family.slug}`,
                {
                  'is-conceptFont': family.conceptFont,
                },
              )}
              style={{
                // @ts-expect-error: Once we upgrade to a newer version of
                // all dependencies that depend on the `csstypes` package, we
                // can remove this and add `--home-page-text-color` to the
                // Properties type in `csstypes`.
                '--home-page-text-color': family.homePageTextColor,
              }}
              onMouseMove={this.handleMouseMove}
            >
              <Link
                className="FamiliesFamilyLink-link"
                style={{
                  fontFamily: `'${family.name} Variable'`,
                  fontVariationSettings: `'wght' ${weight}, 'slnt' ${slant}`,
                  lineHeight: `${family.variableLineHeight}em`,
                  marginBottom: `${family.variableMarginBottom}em`,
                }}
                to={`/families/${family.slug}`}
              >
                {family.name}
              </Link>

              <Translation>
                {(t) => (
                  <div
                    className="FamiliesFamilyLink-details"
                    dangerouslySetInnerHTML={{
                      __html: [
                        family.name.replace(/\s/g, '&nbsp;'),
                        t('family.details.widths', {
                          count: family.widthsCount,
                        }),
                        t('family.details.styles', {
                          count: family.facesCount,
                        }),
                        family.variableFontsCount > 0
                          ? t('family.details.variableFonts', {
                              count: family.variableFontsCount,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' • '),
                    }}
                  />
                )}
              </Translation>
            </div>
          </ColorBar>
        )}
      </Measure>
    );
  }
}

export default FamilyLink;
